import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiFetch } from '../lib/api';

const normalizeDecimalString = (value) => {
  const raw = String(value ?? '');
  const cleaned = raw.replace(/,/g, '.').replace(/[^\d.]/g, '');
  const firstDot = cleaned.indexOf('.');
  if (firstDot === -1) return cleaned;
  return `${cleaned.slice(0, firstDot + 1)}${cleaned.slice(firstDot + 1).replace(/\./g, '')}`;
};

const parseDecimalInput = (value) => {
  const normalized = normalizeDecimalString(value).trim();
  if (normalized === '' || normalized === '.') return null;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
};

export default function AutoBid() {
  const navigate = useNavigate();
  const { groupId } = useParams();
  const gid = useMemo(() => parseInt(groupId, 10), [groupId]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const [group, setGroup] = useState(null);

  // Premium-only memberships for this user in this group
  const [memberships, setMemberships] = useState([]);
  const [membershipIndex, setMembershipIndex] = useState('');

  // Interest percent (e.g. 15 for 15%)
  const [interestPercent, setInterestPercent] = useState('');

  // UI rows
  // { period_number, planned_amount, skipped, override_amount }
  const [periods, setPeriods] = useState([]);

  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Load eligible premium memberships + group details
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setErr('');
      setMsg('');
      try {
        const [eligRes, gRes] = await Promise.all([
          apiFetch('/api/auto-bids/eligible/' + gid),
          apiFetch('/api/groups/' + gid).catch(() => null),
        ]);

        const eligJson = await eligRes.json().catch(() => ({}));
        const gJson = gRes ? await gRes.json().catch(() => ({})) : null;

        if (!alive) return;

        if (!eligRes.ok) {
          setErr(eligJson?.error || 'Failed to load premium memberships');
          setMemberships([]);
          setGroup(gJson?.group || gJson || null);
          return;
        }

        const list = Array.isArray(eligJson?.memberships) ? eligJson.memberships : [];
        setMemberships(list);
        setGroup(gJson?.group || gJson || null);

        if (list.length > 0) {
          setMembershipIndex((prev) => (prev ? prev : String(list[0].membership_index ?? 0)));
        } else {
          setErr('AutoBid is available only for premium memberships in this group.');
        }
      } catch (e) {
        if (!alive) return;
        setErr(e?.message || 'Failed to load AutoBid');
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [gid]);

  // Load saved plan when membership changes
  useEffect(() => {
    let alive = true;
    (async () => {
      setMsg('');
      setErr('');
      setPeriods([]);
      if (!membershipIndex) return;

      try {
        const res = await apiFetch(
          '/api/auto-bids/' + gid + '?membership_index=' + encodeURIComponent(membershipIndex)
        );
        const json = await res.json().catch(() => ({}));
        if (!alive) return;

        if (!res.ok) {
          setErr(json?.error || 'Failed to load saved plan');
          return;
        }

        if (!json?.exists) {
          setPeriods([]);
          return;
        }

        const header = json?.header || {};
        const items = Array.isArray(json?.items) ? json.items : [];

        // We store interest percent in auto_bids.total_interest for schema compatibility.
        setInterestPercent(header?.total_interest != null ? String(header.total_interest) : '');

        const org = Number(group?.organiser_benefit_period || 0) || null;
        const dur = Number(group?.duration || 0) || null;

        const normalized = items
          .slice()
          .sort((a, b) => Number(a.period_number) - Number(b.period_number))
          .map((r) => {
            const p = Number(r.period_number);
            const skipped = (org && p === org) || (dur && p === dur);
            return {
              period_number: p,
              planned_amount: r.planned_amount != null ? Number(r.planned_amount) : 0,
              skipped,
              override_amount: r.override_amount != null ? Number(r.override_amount) : null,
            };
          });

        setPeriods(normalized);
      } catch (e) {
        if (!alive) return;
        setErr(e?.message || 'Failed to load saved plan');
      }
    })();

    return () => {
      alive = false;
    };
  }, [gid, membershipIndex, group?.organiser_benefit_period, group?.duration]);

  const canInteract = memberships.length > 0 && !!membershipIndex;

  const onGenerate = async () => {
    setMsg('');
    setErr('');

    if (!canInteract) {
      setErr('Premium membership is required to use AutoBid.');
      return;
    }

    const n = parseDecimalInput(interestPercent);
    if (!Number.isFinite(n) || n < 0 || n > 100) {
      setErr('Please enter a valid interest percentage (0 to 100).');
      return;
    }

    setGenerating(true);
    try {
      const res = await apiFetch('/api/auto-bids/' + gid + '/plan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          membership_index: Number(membershipIndex),
          interest_percent: n,
        }),
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr(json?.error || 'Failed to generate plan');
        return;
      }

      const plan = json?.plan;
      const list = Array.isArray(plan?.periods) ? plan.periods : [];

      const normalized = list.map((p) => ({
        period_number: Number(p.period_number),
        planned_amount: p.planned_amount != null ? Number(p.planned_amount) : 0,
        skipped: !!p.skipped,
        override_amount: null,
      }));

      setPeriods(normalized);
      setMsg('Plan generated. You can override individual periods before saving.');
    } catch (e) {
      setErr(e?.message || 'Failed to generate plan');
    } finally {
      setGenerating(false);
    }
  };

  const onChangeOverride = (periodNumber, v) => {
    setPeriods((prev) =>
      prev.map((p) => {
        if (p.period_number !== periodNumber) return p;
        if (p.skipped) return p;

        const trimmed = normalizeDecimalString(v).trim();
        if (trimmed === '' || trimmed === '.') return { ...p, override_amount: null };

        const n = Number(trimmed);
        if (!Number.isFinite(n) || n < 0) return p;
        return { ...p, override_amount: n };
      })
    );
  };

  const onSave = async () => {
    setMsg('');
    setErr('');

    if (!canInteract) {
      setErr('Premium membership is required to use AutoBid.');
      return;
    }

    const n = parseDecimalInput(interestPercent);
    if (!Number.isFinite(n) || n < 0 || n > 100) {
      setErr('Please enter a valid interest percentage (0 to 100).');
      return;
    }

    if (!Array.isArray(periods) || periods.length === 0) {
      setErr('Generate a plan first (or load an existing plan) before saving.');
      return;
    }

    const items = periods
      .slice()
      .sort((a, b) => Number(a.period_number) - Number(b.period_number))
      .map((p) => ({
        period_number: Number(p.period_number),
        planned_amount: Number(p.planned_amount || 0),
        override_amount: p.skipped ? null : p.override_amount,
      }));

    setSaving(true);
    try {
      const res = await apiFetch('/api/auto-bids/' + gid + '/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          membership_index: Number(membershipIndex),
          interest_percent: n,
          items,
        }),
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr(json?.error || 'Failed to save AutoBid plan');
        return;
      }

      setMsg('✅ Saved. AutoBid will run per this premium membership, using overrides where set.');
    } catch (e) {
      setErr(e?.message || 'Failed to save AutoBid plan');
    } finally {
      setSaving(false);
    }
  };

  const money = (val) => {
    if (val == null || !Number.isFinite(Number(val))) return '';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: group?.currency_code || 'AUD',
        maximumFractionDigits: 0,
      }).format(Number(val));
    } catch {
      return String(val);
    }
  };

  const orgP = Number(group?.organiser_benefit_period || 0) || null;
  const dur = Number(group?.duration || 0) || null;

  const skippedLabel = (p) => {
    if (orgP && p === orgP) return 'Organiser benefit (no bidding)';
    if (dur && p === dur) return 'Final period';
    return 'Skipped';
  };

  return (
    <div className="autobid-page">
      <div className="autobid-header">
        <div>
          <h2 className="autobid-title">AutoBid</h2>
          <div className="autobid-subtitle">
            {group?.name ? group.name + ' • ' : ''}Configure AutoBid per premium membership.
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="autobid-backBtn"
        >
          Back
        </button>
      </div>

      {loading && <div style={{ marginTop: 14, color: '#6b7280' }}>Loading…</div>}
      {!loading && err && (
        <div style={{ marginTop: 14, padding: 12, border: '1px solid #fecaca', background: '#fff1f2', borderRadius: 10, color: '#991b1b' }}>
          {err}
        </div>
      )}
      {!loading && msg && (
        <div style={{ marginTop: 14, padding: 12, border: '1px solid #bbf7d0', background: '#f0fdf4', borderRadius: 10, color: '#065f46' }}>
          {msg}
        </div>
      )}

      <div className="autobid-card">
        <div className="autobid-grid">
          <div>
            <div className="autobid-label">Premium membership</div>
            <select
              value={membershipIndex}
              onChange={(e) => setMembershipIndex(e.target.value)}
              disabled={!canInteract || saving || generating}
              className="autobid-control"
            >
              {memberships.map((m) => (
                <option key={m.membership_id || m.membership_index} value={String(m.membership_index ?? 0)}>
                  Membership {m.membership_index ?? 0}
                </option>
              ))}
              {memberships.length === 0 && <option value="">No premium memberships</option>}
            </select>
          </div>

          <div>
            <div className="autobid-label">Interest percentage</div>
            <div className="autobid-interestRow">
              <input
                value={interestPercent}
                onChange={(e) => setInterestPercent(normalizeDecimalString(e.target.value))}
                disabled={!canInteract || saving || generating}
                type="text"
                placeholder="e.g., 12.5"
                inputMode="decimal"
                pattern="[0-9]*[.,]?[0-9]*"
                autoComplete="off"
                enterKeyHint="done"
                className="autobid-control"
              />
              <button
                type="button"
                onClick={onGenerate}
                disabled={!canInteract || saving || generating}
                className="autobid-primaryBtn"
              >
                {generating ? 'Generating…' : 'Generate plan'}
              </button>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 14, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={{ textAlign: 'left', padding: 10, borderBottom: '1px solid #e5e7eb' }}>Period</th>
                <th style={{ textAlign: 'left', padding: 10, borderBottom: '1px solid #e5e7eb' }}>Suggested bid</th>
                <th style={{ textAlign: 'left', padding: 10, borderBottom: '1px solid #e5e7eb' }}>Override</th>
              </tr>
            </thead>
            <tbody>
              {periods.map((p) => (
                <tr key={p.period_number}>
                  <td style={{ padding: 10, borderBottom: '1px solid #f1f5f9' }}>{p.period_number}</td>
                  <td style={{ padding: 10, borderBottom: '1px solid #f1f5f9' }}>
                    {p.skipped ? skippedLabel(p.period_number) : money(p.planned_amount)}
                  </td>
                  <td style={{ padding: 10, borderBottom: '1px solid #f1f5f9' }}>
                    {p.skipped ? (
                      <input
                        disabled
                        value=""
                        placeholder="N/A"
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#f8fafc' }}
                      />
                    ) : (
                      <input
                        value={p.override_amount == null ? '' : String(p.override_amount)}
                        onChange={(e) => onChangeOverride(p.period_number, normalizeDecimalString(e.target.value))}
                        placeholder={money(p.planned_amount)}
                        inputMode="decimal"
                        pattern="[0-9]*[.,]?[0-9]*"
                        disabled={saving}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 10, border: '1px solid #e5e7eb' }}
                      />
                    )}
                  </td>
                </tr>
              ))}

              {periods.length === 0 && (
                <tr>
                  <td colSpan={3} style={{ padding: 12, color: '#6b7280' }}>
                    No plan loaded. Enter interest % and click Generate plan.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="autobid-actions">
          <button
            type="button"
            onClick={onSave}
            disabled={!canInteract || saving || periods.length === 0}
            className="autobid-primaryBtn"
          >
            {saving ? 'Saving…' : 'Save plan'}
          </button>

          <button
            type="button"
            onClick={() => setPeriods([])}
            disabled={saving || generating}
            className="autobid-secondaryBtn"
          >
            Clear
          </button>
        </div>
      </div>
    </div>
  );
}
